/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Outlet } from 'react-router-dom';
import { type FC, useEffect } from 'react';

import { useUpdate } from 'ahooks';
import { BrowserUpgradeWrap } from '@coze-foundation/browser-upgrade-banner';
import { I18nProvider } from '@coze-arch/i18n/i18n-provider';
import { I18n, normalizeLocale, toI18nLanguage } from '@coze-arch/i18n';
import { useUserInfo } from '@coze-arch/foundation-sdk';
import { CDLocaleProvider, ThemeProvider } from '@coze-arch/coze-design';
import { LocaleProvider, enUS, zhCN, zhHK } from '@coze-arch/bot-semi';

import { GlobalLayoutComposed } from '@/components/global-layout-composed';

import { getSSOLocaleOverride } from '../../sso-preferences';
import { enUSDesign, zhCNDesign, zhHKDesign } from './locales';

export const GlobalLayout: FC = () => {
  const userInfo = useUserInfo();
  const update = useUpdate();
  const currentLocale = normalizeLocale(
    getSSOLocaleOverride() ?? userInfo?.locale ?? navigator.language ?? 'en-US',
  );
  const transformedCurrentLocale = toI18nLanguage(currentLocale);

  const cozeDesignLocale =
    currentLocale === 'en-US'
      ? enUSDesign
      : currentLocale === 'zh-HK'
        ? zhHKDesign
        : zhCNDesign;
  const semiLocale =
    currentLocale === 'en-US' ? enUS : currentLocale === 'zh-HK' ? zhHK : zhCN;

  useEffect(() => {
    document.documentElement.lang = currentLocale;
    if (userInfo && I18n.language !== transformedCurrentLocale) {
      localStorage.setItem('i18next', transformedCurrentLocale);
      I18n.setLang(transformedCurrentLocale);
      // Force an update, otherwise the language switch will not take effect
      update();
    }
  }, [currentLocale, userInfo, transformedCurrentLocale, update]);

  return (
    <I18nProvider i18n={I18n}>
      <CDLocaleProvider locale={cozeDesignLocale}>
        <LocaleProvider locale={semiLocale}>
          <ThemeProvider
            defaultTheme="light"
            changeSemiTheme={true}
            changeBySystem={IS_BOE}
          >
            <BrowserUpgradeWrap>
              <GlobalLayoutComposed>
                <Outlet />
              </GlobalLayoutComposed>
            </BrowserUpgradeWrap>
          </ThemeProvider>
        </LocaleProvider>
      </CDLocaleProvider>
    </I18nProvider>
  );
};
